import React from 'react';
import { Badge, Card } from 'react-bootstrap';
import useStudentEmploymentRules from '@/shared/hooks/useStudentEmploymentRules';
import {
    creditHourMinimumFor,
    employmentAttestationFrom,
    formatGpa,
} from '@/shared/utils/studentEmployment';

const HR_VERIFIES = 'HR verifies eligibility when the student is hired.';

const answerText = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return 'Not answered';
};

const internationalText = (answers) => {
    if (answers.isInternational !== true) {
        return answerText(answers.isInternational);
    }
    return answers.studentLevel ? `Yes (${answers.studentLevel})` : 'Yes';
};

/**
 * The student's employment eligibility answers, read-only. `attestation` is
 * a stored one (with rulesSnapshot) or the wizard's answers. `unmet` is the
 * server's employmentUnmet list; pass it only for a reviewer. Without it
 * nothing is marked, so a page that must not flag an answer cannot.
 *
 * `audience` 'faculty' reads the answers a Learning Assistant's faculty are
 * sent: no international status, no weekly hours limit, and labels without
 * the university's numbers (a credit-hour figure would tell a reader which
 * minimum the student answered against).
 */
function EmploymentAttestationSummary({
    attestation,
    unmet,
    audience = 'coordinator',
}) {
    const isFaculty = audience === 'faculty';
    const answers = employmentAttestationFrom(attestation);
    if (isFaculty) {
        // Whatever a response carries, faculty are not shown these
        answers.isInternational = null;
        answers.studentLevel = null;
    }
    const hasAnswers = Object.values(answers).some((v) => v !== null);
    const snapshot = isFaculty ? null : attestation?.rulesSnapshot;
    const { rules: fetchedRules } = useStudentEmploymentRules({
        skip: isFaculty || Boolean(snapshot) || !hasAnswers,
    });
    const rules = snapshot || fetchedRules;

    let body;
    if (!hasAnswers) {
        body = (
            <p className="mb-0">
                No employment eligibility answers on this application.
            </p>
        );
    } else {
        const creditMinimum = creditHourMinimumFor(rules, answers);
        const rows = [
            {
                key: 'authorizedToWork',
                label: 'Authorized to work in the United States',
                answer: answerText(answers.authorizedToWork),
            },
            {
                key: 'minimumAge',
                label: rules
                    ? `At least ${rules.minimumAge} years old`
                    : 'At least the minimum age',
                answer: answerText(answers.meetsMinimumAge),
            },
            !isFaculty && {
                key: 'international',
                label: 'International student',
                answer: internationalText(answers),
            },
            {
                key: 'creditHours',
                label:
                    creditMinimum !== null
                        ? `Registered for at least ${creditMinimum} credit hours`
                        : 'Registered for the minimum credit hours',
                answer: answerText(answers.meetsCreditHours),
            },
            answers.meetsCreditHours === false && {
                key: 'graduated',
                label: 'Graduated',
                answer: answerText(answers.graduatingSemesterException),
            },
            {
                key: 'semesterGpa',
                label: rules
                    ? `Semester GPA of at least ${formatGpa(rules.minimumSemesterGpa)}`
                    : 'Semester GPA at or above the minimum',
                answer: answerText(answers.meetsSemesterGpa),
            },
        ].filter(Boolean);

        let flagLine = null;
        if (Array.isArray(unmet)) {
            flagLine = unmet.length
                ? `By the student's own answers, ${unmet.length} ${unmet.length === 1 ? 'requirement is' : 'requirements are'} not met. ${HR_VERIFIES}`
                : `The student's answers meet every requirement. ${HR_VERIFIES}`;
        }

        body = (
            <>
                {flagLine && <p className="mb-2">{flagLine}</p>}
                <dl className="mb-2">
                    {rows.map(({ key, label, answer }) => (
                        <div className="row" key={key}>
                            <dt className="col-sm-7 fw-normal">{label}</dt>
                            <dd className="col-sm-5 mb-1">
                                {answer}
                                {Array.isArray(unmet) &&
                                    unmet.includes(key) && (
                                        <Badge
                                            bg="warning"
                                            text="dark"
                                            className="ms-2"
                                        >
                                            Requirement not met
                                        </Badge>
                                    )}
                            </dd>
                        </div>
                    ))}
                </dl>
                {!isFaculty && answers.isInternational === true && (
                    <p className="mb-2">
                        {rules
                            ? `The international weekly hours limit (${rules.internationalWeeklyHoursLimit}) applies.`
                            : 'The international weekly hours limit applies.'}
                    </p>
                )}
                {attestation?.confirmedAt && (
                    <small className="text-muted d-block">
                        Answered on{' '}
                        {new Date(attestation.confirmedAt).toLocaleDateString()}
                    </small>
                )}
            </>
        );
    }

    return (
        <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="py-2">
                <strong className="mb-2 d-block">Employment eligibility</strong>
                {body}
            </Card.Body>
        </Card>
    );
}

export default EmploymentAttestationSummary;
